import { create } from 'zustand';

// Minimal UI-facing connectivity state. Mirrors the sync-store /
// bsb-progress-store pattern: a tiny dedicated slice kept separate from the
// big useAdiaStore so connectivity truth does not entangle with prayer state.
//
// Convention (honored app-wide): navigator.onLine === true is NOT trusted as
// "definitely online" anywhere in this codebase. It is trusted only as
// "definitely offline" when false. So consumers of this store should ACT only
// on `!online` and treat `online === true` as the safe default (behave exactly
// as today). A flaky-network false-positive can then only ever under-claim
// offline, never falsely tell the user they are offline.
//
// The seeded default is `true` so SSR / pre-listener boot behaves as online
// (the safe default), and the window listeners flip the flag thereafter. These
// listeners coexist with main.tsx's existing fallbackReason listeners: a
// different concern (voice routing vs UI truth), so this store does not touch
// that wiring.
interface OnlineState {
  online: boolean;
  setOnline: (value: boolean) => void;
}

export const useOnlineStore = create<OnlineState>((set) => ({
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  setOnline: (value) => set({ online: value }),
}));

// Module-load listeners, boot-safe (usable before React mounts, same posture
// as the existing main.tsx listeners). Plain listeners, no throw.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useOnlineStore.getState().setOnline(true);
  });
  window.addEventListener('offline', () => {
    useOnlineStore.getState().setOnline(false);
  });
}

/**
 * Non-reactive snapshot for one-shot reads (e.g. branching a save status at
 * save time). The settings-store getX() convention.
 */
export function getOnline(): boolean {
  return useOnlineStore.getState().online;
}
